import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { sanitizeHtml } from '@/utils/helpers/html'
import Breadcrumb from '@/components/ui/Breadcrumb'
import Pagination from '@/components/ui/Pagination'
import {
  usePublications,
  useIncrementPublicationDownload,
  useIncrementPublicationView,
  useSearchPublications
} from '@/lib/api/hooks/publication'
import { useAllWebPages } from '@/lib/api/hooks/web-pages'
import FullPageSpinner from '@/components/ui/FullPageSpinner'
import { motion } from 'framer-motion'
import { fadeUp, staggerContainer, viewportReveal } from '@/lib/motion'
import { parseHtmlString } from '@/utils/helpers/html'
import handleDownloadFromUrl from '@/utils/download'

import Search from '@/components/ui/Search'
import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay'
import FILTER_SECTIONS from '@/components/pages/Resources/Publications/FilterModule/FILTER_SECTIONS'
import FilterModule from '@/components/ui/FilterModule'
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams'

// Format date helper
const formatDate = (dateString?: string | null ) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
}

// Default placeholder image for publications
const DEFAULT_PUBLICATION_IMAGE = 'https://res.cloudinary.com/dlwegkhme/image/upload/v1755762000/fb7ee40b478a3ecbe99ced8825e13470a61ce7d0_xze0jx.png'

export default function Publications() {
  const { data, isFetching, isError, refetch } = useAllWebPages();
  const navigate = useNavigate()
  const publicationsWebpageData = data?.data && data?.data.find((page) =>
    page.codeName?.toLowerCase() === 'resources' ||
    page.websitePageName?.toLowerCase() === 'resources'
  )?.content || null;

  // ── URL-driven search / filter state ──────────────────────────────────────
  const {
    mode,
    searchTerm,
    filterState,
    page,
    size,
    setSearch,
    setFilters,
    removeFilter,
    clearFilters,
    setPage,
    apiFilters,
    getActiveFilterCount
  } = useSearchFilterParams(FILTER_SECTIONS)


  // ── Search query (only active when mode === 'search') ─────────────────────
  const { data: searchData, isLoading: isSearchLoading, error: searchError, refetch: refetchSearchData } = useSearchPublications(
    searchTerm,
    { page, size }
  )

  // ── Filter / default query ─────────────────────────────────────────────────
  const { data: defaultData, isFetching: isDefaultLoading, error: defaultError, refetch: refetchDefaultData } = usePublications(
    { page, size },
    apiFilters
  )

  // ── Select the right data source based on mode ────────────────────────────
  const publicationsData = mode === 'search' ? searchData : defaultData
  const isLoading   = mode === 'search' ? isSearchLoading : isDefaultLoading
  const error       = mode === 'search' ? searchError     : defaultError
  const refetchSearchFilter      = mode === 'search' ? refetchSearchData : refetchDefaultData

  const publications    = Array.isArray(publicationsData?.data?.data) && publicationsData.data.data.length > 0 ? publicationsData.data.data.map((pub) => ({
    ...pub,
    id: pub.publicationId,
    category: pub.publicationCategory.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    date: formatDate(pub.createdAt),
    image: pub.coverImageUrl ?? DEFAULT_PUBLICATION_IMAGE,
  })) : [] 
  const totalPages    = publicationsData?.data?.totalPage ?? 1
  const activeFilterCount = getActiveFilterCount(FILTER_SECTIONS) 

 

    const incrementDownloadMutation = useIncrementPublicationDownload()
  const incrementViewMutation = useIncrementPublicationView()

  const [downloadingPublications, setDownloadingPublications] = useState<Set<string>>(new Set())

  const handleDownload = async (publicationId: string, publicationUrl: string, title?: string) => {
    // Set loading state for this specific publication
    setDownloadingPublications(prev => {
      const newSet = new Set(prev)
      newSet.add(publicationId)
      return newSet
    })

    try {
      // Increment download count
      await incrementDownloadMutation.mutateAsync(publicationId)

      handleDownloadFromUrl({url: publicationUrl, fileName:title ?? 'publication'})
    } catch (error) {
      console.error('Error downloading publication:', error)
      // Fallback: open in new tab
      window.open(publicationUrl, '_blank', 'noopener,noreferrer')
    } finally {
      // Remove loading state for this specific publication
      setDownloadingPublications(prev => {
        const newSet = new Set(prev)
        newSet.delete(publicationId)
        return newSet
      })
    }
  }

  const handleView = async (publicationId: string, publicationUrl: string) => {
    try {
      // Increment view count
      await incrementViewMutation.mutateAsync(publicationId)

      // Open in new tab
      window.open(publicationUrl, '_blank')
    } catch (error) {
      console.error('Error viewing publication:', error)
      // Still try to view even if increment fails
      window.open(publicationUrl, '_blank')
    }
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Publication', href: '', active: true }
  ]

  if (isFetching) {
    return <FullPageSpinner />;
  }

  if (isError) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Error loading page.</p>
      <button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded">Retry</button>
    </div>;
  }

  if (!publicationsWebpageData) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Page content not found.</p>
    </div>;
  }

  return (
    <motion.div
      className="bg-white min-h-screen"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      {/* Breadcrumb */}
      <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" variants={fadeUp}>
        <Breadcrumb items={breadcrumbItems} />
      </motion.div>

      {/* Hero Image */}
      <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8" variants={fadeUp}>
        <img
          src={publicationsWebpageData?.PUBLICATION_BANNER?.fileUrl ?? "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760297/436e364d5664edaf65624f6162f6c339235305d5_1_q2hero.png"}
          alt="African Space Agency Building"
          className="w-full h-[300px] md:h-[480px] object-cover"
        />
      </motion.div>

      {/* Main Content */}
      <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-16" variants={fadeUp}>
        {/* Page Header */}
        <div className="flex flex-col gap-6 mb-16">
          <h1 className="text-4xl font-normal font-['PT_Serif'] leading-[48px] text-gray-900">{publicationsWebpageData?.PUBLICATION?.title ?? "Publications"}</h1>
          <div className="max-w-[1051px] flex flex-col gap-8">
            <p className="text-lg text-gray-900 leading-relaxed">
              <span dangerouslySetInnerHTML={parseHtmlString(publicationsWebpageData?.PUBLICATION?.description ?? "The African Space Agency operates within a robust legal framework—anchored by the African Space Policy, Space Strategy, and Agency Act—that defines its purpose and scope, while enabling it to foster international partnerships and promote the responsible use of space for Africa's development.")} />
            </p>
          </div>
        </div>

        {/* Content Layout */}
        <div className="flex flex-col gap-12">
          {/* Sidebar */}
          {/* Search + Filter Controls */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center relative gap-4 justify-between flex-wrap">
              <Search
                value={searchTerm}
                onSearch={setSearch}
                placeholder="Search events..."
              />
               <FilterModule
                    sections={FILTER_SECTIONS}
                    activeFilters={filterState}
                    onApply={setFilters}
                    activeFilterCount={activeFilterCount}
                  />
            </div>

            {/* Active filter chips — only shown in filter mode */}
            <FilterParamsDisplay
              sections={FILTER_SECTIONS}
              filterState={filterState}
              onRemove={removeFilter}
              onClearAll={clearFilters}
            />
          </div>

          {/* Main Content - Publications Grid */}
          <div className="flex-1 ">
            <div className="flex flex-col gap-10">
              {/* Loading State */}
              {isLoading && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {[1, 2, 3, 4, 5, 6].map((index) => (
                    <div key={index} className="flex flex-col gap-4 animate-pulse">
                      <div className="w-full h-64 bg-gray-200 rounded"></div>
                      <div className="flex flex-col gap-4">
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                        <div className="h-6 bg-gray-200 rounded"></div>
                        <div className="h-4 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {
                 error ? (
              <div className="text-center py-20">
                <p className="text-sm text-red-500">Error loading events. Please try again later.</p>
                <button onClick={() => refetchSearchFilter()} className="mt-4 px-4 py-2 bg-forestGreen-100 text-white rounded">Retry</button>
              </div>
            ) : null
              }

              {/* Publications Grid - 3 columns */}
              {publications.length > 0 ? (
                <>
                    <motion.div
                      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                      variants={staggerContainer}
                      initial="hidden"
                      whileInView="visible"
                      viewport={viewportReveal}
                    >
                      {publications.map((publication) => (
                        <motion.div
                          key={publication.id}
                          className="flex flex-col gap-4 h-full"
                          variants={fadeUp}
                          whileHover={{ y: -4 }}
                          transition={{ type: 'spring', stiffness: 280, damping: 24 }}
                        >
                          {/* Clickable area for navigation */}
                          <div
                            onClick={() => navigate(`/publications/${publication.publicationId}`)}
                            className="cursor-pointer group"
                          >
                            {
                              publication.image ?  <img
                              src={publication.image}
                              alt={publication.title}
                              className="w-full h-64 object-cover group-hover:opacity-90 transition-opacity"
                            /> : <div className="w-full h-64 bg-gray-200 flex items-center justify-center">
                              <span className="text-gray-500">No Image Available</span>
                            </div>
                            }
                           
                          </div>
                          <div className="flex flex-col gap-6 flex-1">
                            <div className="flex flex-col gap-4 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-lg text-gray-500 leading-relaxed">{publication.category}</span>
                                {publication.date && (
                                  <div className="flex items-center gap-2">
                                    <div className="w-0.5 h-4 bg-gray-500 rounded-full"></div>
                                    <span className="text whitespace-nowrap text-gray-500 leading-relaxed">{publication.date}</span>
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col gap-3">
                                <h3
                                  onClick={() => navigate(`/publications/${publication.publicationId}`)}
                                  className="text-lg font-semibold leading-relaxed text-gray-900 line-clamp-2 cursor-pointer hover:text-wine-100 transition-colors"
                                >
                                  {publication.title}
                                </h3>
                                <div
                                  className="text-lg leading-relaxed text-gray-900 line-clamp-3 prose prose-sm max-w-none"
                                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(publication.description) }}
                                />
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mt-auto pt-4">
                              {publication.isExternalSource ? (
                                // External source - show "Open Link" button
                                <button
                                  onClick={() => {
                                    incrementViewMutation.mutate(publication.publicationId)
                                    window.open(publication.externalLink || publication.publicationUrl, '_blank', 'noopener,noreferrer')
                                  }}
                                  className="px-6 py-2.5 bg-wine-100 text-white rounded font-bold text-base hover:bg-wine-200 flex items-center gap-2 transition-colors"
                                >
                                  Open Link
                                </button>
                              ) : (
                                // File upload - show download and view buttons
                                <>
                                  <button
                                    onClick={() => handleDownload(publication.publicationId, publication.publicationUrl, publication.title)}
                                    disabled={downloadingPublications.has(publication.publicationId)}
                                    className="px-6 py-2.5 bg-wine-100 text-white rounded font-bold text-base hover:bg-wine-200 flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {downloadingPublications.has(publication.publicationId) ? 'Downloading...' : 'Download'}
                                  </button>
                                  <button
                                    onClick={() => handleView(publication.publicationId, publication.publicationUrl)}
                                    disabled={incrementViewMutation.isPending}
                                    className="px-4 py-2.5 text-gray-900 font-medium text-base hover:bg-gray-50 flex items-center gap-2 transition-colors rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    View
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
  
                    <Pagination
                      currentPage={page}
                      totalPages={totalPages}
                      onPageChange={setPage}
                    />
                </>
              ) : (
                    <div className="text-center py-12">
                      <p className="text-lg text-gray-600">No publications found matching your criteria.</p>
                    </div>
                  )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}